import PropTypes from "prop-types"
import React, { useState, useEffect, memo } from "react"
import { useStore } from "../../teafuljs/store"
import { getTranslation } from "../../scripts/getTranslation"
import { getMetadata } from "../../scripts/getMetadata"
import { navigate } from "gatsby"

import { makeStyles } from "@material-ui/core/styles"
import InputBase from "@material-ui/core/InputBase"
import MenuItem from "@material-ui/core/MenuItem"
import FormControl from "@material-ui/core/FormControl"
import Select from "@material-ui/core/Select"
import Button from "@material-ui/core/Button"
import bibleBookList from "../../data/BookChapterList.json"

const useStyles = makeStyles({
  toolBar: {
    justifyContent: "center",
  },
  appBar: {
    top: "auto",
    bottom: 0,
    padding: "5px 0",
  },
  languageSelect: {
    // backgroundColor: 'white',
    borderRadius: "2px",
    padding: "0 5px",
    width: "220px",
    height: "35px",
  },
  languageButton: {
    height: "35px",
  },
  footerNavContainer: {
    width: "300px",
    display: "flex",
    justifyContent: "center",
    height: "55px",
    borderRadius: "5px",
    padding: "10px",
    backgroundColor: "#3A4C7D",
    position: "fixed",
    top: "auto",
    bottom: "0.5em",
    left: "calc(50vw - 150px)",
  },
  form: {
    margin: 0,
    minWidth: 120,
  },
  formControl: {
    margin: 0,
  },
  select: {
    color: "white",
    WebkitAppearance: "none",
    textIndent: "1px",
    textAlign: "center",
    verticalAlign: "middle",
    textOverflow: "",
    "&:before": {
      WebkitAppearance: "none",
    },
    "&:after": {
      WebkitAppearance: "none",
    },
  },
  icon: {
    fill: "white",
  },
  menuItem: {
    position: "relative",
    display: "flex",
  },
})

const SimpleSelect = memo(() => {
  const translations = useStore.translations()[0]
  const AvailableContent = useStore.AvailableContent()[0]
  const [
    language,
    setLanguage,
  ] = useStore.language()
  const [
    version,
    setVersion,
  ] = useStore.version()
  const [
    history,
    setHistory,
  ] = useStore.history()
  const [
    searchID,
    setSearchID,
  ] = useStore.searchID()
  const [
    currentSegmentID,
    setCurrentSegmentID,
  ] = useStore.currentSegmentID()
  const [
    metadata,
    setMetadata,
  ] = useStore.metadata()
  const [
    currentBook,
    setCurrentBook,
  ] = useStore.currentBook()
  const classes = useStyles()
  const [
    languageDisplay,
    updateLanguageDisplay,
  ] = useState(language)

  useEffect(() => {
    if (language !== languageDisplay) {
      updateLanguageDisplay(language)
    }
  }, [language])

  const getLangOptions = () => {
    let LanguageOptions = []
    const languageKeys = Object.keys(
      AvailableContent.content
    )
    languageKeys.forEach(key => {
      const langObj = {
        label:
          AvailableContent.content[key]
            .language_name,
        value: key,
        language_code:
          AvailableContent.content[key]
            .language_code,
      }
      LanguageOptions.push(langObj)
    })
    return LanguageOptions.sort(function(a, b) {
      var textA = a.label.toUpperCase()
      var textB = b.label.toUpperCase()
      return textA < textB
        ? -1
        : textA > textB
        ? 1
        : 0
    })
  }

  const getVersionOptions = langKey => {
    let VersionOptions = []
    const versionKeys = Object.keys(
      AvailableContent.content[langKey].versions
    )
    versionKeys.forEach(key => {
      const versionObj = {
        label:
          AvailableContent.content[langKey]
            .versions[key],
        value: key,
      }
      VersionOptions.push(versionObj)
    })
    return VersionOptions
  }

  function findFirstAvailableBook(
    language,
    version
  ) {
    const booksInOrder = Object.keys(
      bibleBookList
    )
    let firstBook = ""
    booksInOrder.forEach(book => {
      if (
        AvailableContent.content[language][
          version
        ][book] !== undefined &&
        firstBook === ""
      ) {
        firstBook = book
      }
    })
    return firstBook
  }

  const storeMetadata = async (
    language,
    version
  ) => {
    const metadata = await getMetadata(
      language,
      version
    )
    setMetadata(metadata)
  }

  function handleChange(event) {
    setHistory([
      {
        page: window.location.pathname,
        language: language,
        version: version,
        segment: currentSegmentID,
        scrollY: window.scrollY,
      },
      ...history,
    ])
    const versionID = getVersionOptions(
      event.target.value
    )[0].value

    const firstBookOfNewLanguage = findFirstAvailableBook(
      event.target.value,
      versionID
    )
    const firstSegmentOfNewLanguage =
      AvailableContent.content[
        event.target.value
      ][versionID][firstBookOfNewLanguage]
        .segments[0]
    setCurrentSegmentID(
      firstSegmentOfNewLanguage
    )
    setCurrentBook(firstBookOfNewLanguage)
    setVersion(versionID)
    setLanguage(event.target.value)
    storeMetadata(event.target.value, versionID)
    updateLanguageDisplay(event.target.value)
  }

  // Create flag from country code
  function getCountryFlag(cc) {
    // Mild sanity check.
    if (cc.length !== 2) return cc

    // Convert char to Regional Indicator Symbol Letter
    function risl(chr) {
      return String.fromCodePoint(
        0x1f1e6 -
          65 +
          chr.toUpperCase().charCodeAt(0)
      )
    }
    // Create RISL sequence from country code.
    return risl(cc[0]) + risl(cc[1])
  }

  return (
    <div>
      {!AvailableContent.content ? null : (
        <form
          className={classes.root}
          autoComplete="off"
        >
          <FormControl
            variant="outlined"
            className={classes.formControl}
          >
            <Select
              value={languageDisplay}
              onChange={handleChange}
              className={classes.select}
              inputProps={{
                classes: {
                  icon: classes.icon,
                },
              }}
              input={
                <InputBase
                  className={classes.input}
                />
              }
            >
              {getLangOptions().map(
                (language, index) => (
                  <MenuItem
                    key={index}
                    value={language.value}
                  >
                    {`${getCountryFlag(
                      language.language_code
                    )} ${language.label}`}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        </form>
      )}
    </div>
  )
})

const FooterNav = memo(() => {
  const translations = useStore.translations()[0]
  const AvailableContent = useStore.AvailableContent()[0]
  const language = useStore.language()[0]
  const version = useStore.version()[0]
  const [history, setHistory] = useStore.history()
  const [searchID, setSearchID] = useStore.searchID()
  const [currentSegmentID, setCurrentSegmentID] = useStore.currentSegmentID()
  const [currentBook, setCurrentBook] = useStore.currentBook()
  const classes = useStyles()
  // This controls the visibility of chapter and verse references

  function findFirstAvailableBook(language, version) {
    const booksInOrder = Object.keys(bibleBookList)
    let firstBook = ""
    booksInOrder.forEach(book => {
      if (
        AvailableContent.content[language][version][book] !== undefined &&
        firstBook === ""
      ) {
        firstBook = book
      }
    })
    return firstBook
  }

  function navigateToReader() {
    setHistory([
      {
        page: window.location.pathname,
        language: language,
        version: version,
        segment: currentSegmentID,
        scrollY: window.scrollY,
      },
      ...history,
    ])
    if (
      AvailableContent.content[language][version][
        currentBook
      ] &&
      AvailableContent.content[language][version][
        currentBook
      ].segments.indexOf(currentSegmentID) !== -1
    ) {
      navigate("/reader/")
    } else {
      const firstBookOfNewLanguage = findFirstAvailableBook(
        language,
        version
      )
      const firstSegmentOfNewLanguage =
        AvailableContent.content[language][version][
          firstBookOfNewLanguage
        ].segments[1]
      setCurrentSegmentID(firstSegmentOfNewLanguage)
      setCurrentBook(firstBookOfNewLanguage)
      navigate("/reader")
    }
  }

  return (
    <div>
      {AvailableContent && (
        <div className={classes.footerNavContainer}>
          <div className={classes.languageSelect}>
            <SimpleSelect data-cy="footerSelect" />
          </div>
          <Button
            style={{ width: 120 }}
            data-cy="footerRead"
            size="small"
            color="secondary"
            variant="contained"
            className={classes.languageButton}
            onClick={() => navigateToReader()}
          >
            {getTranslation(
              translations,
              language,
              "UI",
              "footer_nav_read"
            )}
          </Button>
        </div>
      )}
    </div>
  )
})

FooterNav.propTypes = {
  siteTitle: PropTypes.string,
}

FooterNav.defaultProps = {
  siteTitle: ``,
}

export default FooterNav
